import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Plus, Minus, Check, Trash2, Pause, Play, ListChecks, Package } from 'lucide-react';

export default function QueuePage() {
  const [queue, setQueue] = useState([]);
  const [completedQueue, setCompletedQueue] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const navigate = useNavigate();

  const loadQueue = useCallback(() => {
    api.get('/queue?status=active').then(r => setQueue(r.data.queue));
    api.get('/queue?status=completed').then(r => setCompletedQueue(r.data.queue));
  }, []);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const updateProgress = async (queueId, partId, increment, subPartId = null) => {
    await api.put(`/queue/${queueId}/progress`, { part_id: partId, sub_part_id: subPartId, increment });
    loadQueue();
  };

  const setProgressCount = async (queueId, partId, count, subPartId = null) => {
    await api.put(`/queue/${queueId}/progress`, { part_id: partId, sub_part_id: subPartId, set_count: count });
    loadQueue();
  };

  const deleteQueueItem = async (queueId) => {
    if (!window.confirm('Remove from queue?')) return;
    await api.delete(`/queue/${queueId}`);
    loadQueue();
  };

  const togglePause = async (item) => {
    await api.put(`/queue/${item.id}`, { status: item.status === 'paused' ? 'active' : 'paused' });
    loadQueue();
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1>Print Queue</h1>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => setShowCompleted(!showCompleted)}>
            {showCompleted ? 'Hide' : 'Show'} Completed ({completedQueue.length})
          </button>
        </div>
      </div>

      {queue.length === 0 && !showCompleted ? (
        <div style={{ textAlign: 'center', padding: '80px 20px', color: 'var(--text-muted)' }}>
          <ListChecks size={56} style={{ marginBottom: '16px', opacity: 0.2 }} />
          <p style={{ fontSize: '18px', marginBottom: '8px' }}>Print queue is empty</p>
          <p style={{ fontSize: '14px' }}>Go to a model and click "Add to Queue" to start tracking prints</p>
        </div>
      ) : (
        <>
          {queue.map(item => (
            <div key={item.id} className="queue-item">
              <div className="queue-item-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span className="queue-item-title" style={{ cursor: 'pointer' }} onClick={() => navigate(`/models/${item.model_id}`)}>
                    {item.model_name}
                  </span>
                  <span className="badge badge-accent">{item.target_quantity}x target</span>
                </div>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <button className="btn-icon" onClick={() => togglePause(item)} title={item.status === 'paused' ? 'Resume' : 'Pause'}>
                    {item.status === 'paused' ? <Play size={16} /> : <Pause size={16} />}
                  </button>
                  <button className="btn-icon" onClick={() => deleteQueueItem(item.id)} title="Remove">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Overall progress */}
              <div className="progress-bar" style={{ marginBottom: '16px', height: '10px' }}>
                <div className={`progress-bar-fill ${item.overall_progress === 100 ? 'complete' : ''}`} style={{ width: `${item.overall_progress}%` }} />
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                Overall: {item.overall_progress}% complete
              </div>

              {/* Per-part progress (with sub-part grouping) */}
              <div style={{ display: 'grid', gap: '8px' }}>
                {(() => {
                  const grouped = {};
                  (item.progress || []).forEach(p => {
                    if (!grouped[p.part_name]) grouped[p.part_name] = [];
                    grouped[p.part_name].push(p);
                  });
                  return Object.entries(grouped).map(([partName, rows]) => (
                    <div key={partName} style={{ marginBottom: '2px' }}>
                      {rows[0].sub_part_name && (
                        <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)',
                                      padding: '4px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {partName} ({rows[0].file_type?.toUpperCase()})
                        </div>
                      )}
                      {rows.map(p => {
                        const pct = p.needed_count > 0 ? Math.round((p.printed_count / p.needed_count) * 100) : 0;
                        const isDone = p.printed_count >= p.needed_count;
                        return (
                          <div key={p.id || `${p.part_id}-${p.sub_part_id}`} style={{
                            display: 'flex', alignItems: 'center', gap: '12px',
                            padding: '10px 14px',
                            background: isDone ? 'var(--success-dim)' : 'var(--bg-tertiary)',
                            borderRadius: 'var(--radius-sm)',
                            border: `1px solid ${isDone ? 'var(--success)' : 'var(--border)'}`,
                            marginBottom: '4px',
                            opacity: isDone ? 0.7 : 1
                          }}>
                            {p.sub_part_color && (
                              <div style={{
                                width: '14px', height: '14px', borderRadius: '50%', flexShrink: 0,
                                background: p.sub_part_color, border: '2px solid rgba(255,255,255,0.2)'
                              }} title={`Extruder ${p.sub_part_extruder}`} />
                            )}
                            {isDone ? <Check size={18} color="var(--success)" /> : <Package size={18} color="var(--text-muted)" />}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: '500', fontSize: '14px' }}>
                                {p.sub_part_name || p.part_name}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                <div className="progress-bar" style={{ flex: 1, height: '6px' }}>
                                  <div className={`progress-bar-fill ${isDone ? 'complete' : ''}`} style={{ width: `${pct}%` }} />
                                </div>
                                <span style={{ fontSize: '13px', fontWeight: '600', whiteSpace: 'nowrap',
                                               color: isDone ? 'var(--success)' : 'var(--text-primary)' }}>
                                  {p.printed_count} / {p.needed_count}
                                </span>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button className="btn-icon" style={{ padding: '6px' }}
                                onClick={() => updateProgress(item.id, p.part_id, -1, p.sub_part_id)}
                                disabled={p.printed_count <= 0}>
                                <Minus size={14} />
                              </button>
                              <button className="btn-icon" style={{ padding: '6px',
                                background: isDone ? 'var(--success-dim)' : 'var(--accent-dim)',
                                color: isDone ? 'var(--success)' : 'var(--accent)' }}
                                onClick={() => updateProgress(item.id, p.part_id, 1, p.sub_part_id)}
                                disabled={isDone}>
                                <Plus size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}
              </div>
            </div>
          ))}

          {/* Completed items */}
          {showCompleted && completedQueue.length > 0 && (
            <div style={{ marginTop: '32px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '14px', color: 'var(--text-secondary)' }}>Completed</h3>
              {completedQueue.map(item => (
                <div key={item.id} className="queue-item" style={{ opacity: 0.6 }}>
                  <div className="queue-item-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Check size={20} color="var(--success)" />
                      <span className="queue-item-title">{item.model_name}</span>
                      <span className="badge badge-success">{item.target_quantity}x done</span>
                    </div>
                    <button className="btn-icon" onClick={() => deleteQueueItem(item.id)}><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
